import React, {useState} from "react";
import {
    Box, Typography, Paper, CircularProgress, Divider,
    Table, TableBody, TableCell, TableContainer, TableRow,
} from "@mui/material";
import {accountingApi} from "../../services/api/accounting";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import ReportFilters from "./ReportFilters";

const formatMoney = (amount) => {
    if (amount === null || amount === undefined) return "KES 0.00";
    const num = Number(amount);
    const formatted = Math.abs(num).toLocaleString("en-KE", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return num < 0 ? `(KES ${formatted})` : `KES ${formatted}`;
};

function Section({title, items, total, color}) {
    return (
        <Box sx={{px: 3, py: 2}}>
            <Typography variant="subtitle1" fontWeight={600} sx={{color, mb: 1}}>{title}</Typography>
            {items?.length > 0 ? (
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            {items.map((item, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{border: "none", pl: 2}}>{item.code} - {item.name}</TableCell>
                                    <TableCell align="right" sx={{border: "none", fontWeight: 500}}>{formatMoney(item.balance)}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{fontWeight: 700, borderTop: `2px solid ${color}`, color}}>Total {title}</TableCell>
                                <TableCell align="right" sx={{fontWeight: 700, borderTop: `2px solid ${color}`, color}}>{formatMoney(total)}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : <Typography variant="body2" color="textSecondary" sx={{pl: 2}}>No {title.toLowerCase()} to display.</Typography>}
        </Box>
    );
}

export default function BalanceSheet() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const handleApply = async (params) => {
        setLoading(true);
        try {
            const response = await accountingApi.getBalanceSheet(params);
            if (response.ok && response.data) {
                setData(response.data);
            } else {
                setData(null);
            }
        } catch (err) {
            console.error("Failed to fetch balance sheet:", err);
        } finally {
            setLoading(false);
        }
    };

    const handlePDF = async (params) => {
        setDownloading(true);
        try {
            const response = await accountingApi.downloadBalanceSheetPDF(params);
            if (response.ok && response.blob) {
                const url = window.URL.createObjectURL(response.blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Balance_Sheet_as_of_${params.end_date}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            } else {
                showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Failed to download PDF.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{py: 3}}>
            <Typography variant="h5" fontWeight={600} sx={{mb: 3, color: "#2C5283"}}>
                Balance Sheet
            </Typography>
            <ReportFilters onApply={handleApply} onDownloadPDF={handlePDF} downloading={downloading} hideDateStart/>

            {loading ? (
                <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
            ) : !data ? (
                <Paper sx={{py: 6, textAlign: "center", borderRadius: 2}}>
                    <Typography color="textSecondary">Select a date and click Apply to generate the report.</Typography>
                </Paper>
            ) : (
                <Paper sx={{borderRadius: 2, overflow: "hidden"}}>
                    {data.as_of && (
                        <Box sx={{px: 3, py: 1.5, bgcolor: "#f5f7fa", borderBottom: "1px solid #e0e0e0"}}>
                            <Typography variant="body2" color="textSecondary">As of: {data.as_of}</Typography>
                        </Box>
                    )}

                    <Section title="Assets" items={data.assets} total={data.total_assets} color="#1565c0"/>
                    <Divider/>
                    <Section title="Liabilities" items={data.liabilities} total={data.total_liabilities} color="#c62828"/>
                    <Divider/>

                    {/* Equity */}
                    <Box sx={{px: 3, py: 2}}>
                        <Typography variant="subtitle1" fontWeight={600} sx={{color: "#2e7d32", mb: 1}}>Equity</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{border: "none", pl: 2}}>Owner's Equity</TableCell>
                                        <TableCell align="right" sx={{border: "none", fontWeight: 500}}>{formatMoney(data.equity?.owners_equity)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{border: "none", pl: 2}}>Retained Earnings</TableCell>
                                        <TableCell align="right" sx={{border: "none", fontWeight: 500}}>{formatMoney(data.equity?.retained_earnings)}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 700, borderTop: "2px solid #2e7d32", color: "#2e7d32"}}>Total Equity</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 700, borderTop: "2px solid #2e7d32", color: "#2e7d32"}}>{formatMoney(data.total_equity)}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Divider/>

                    {/* Accounting Equation Check */}
                    <Box sx={{px: 3, py: 2, bgcolor: "#f5f7fa"}}>
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="body2" fontWeight={600}>Total Assets</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatMoney(data.total_assets)}</Typography>
                        </Box>
                        <Box sx={{display: "flex", justifyContent: "space-between"}}>
                            <Typography variant="body2" fontWeight={600}>Total Liabilities + Equity</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatMoney((data.total_liabilities || 0) + (data.total_equity || 0))}</Typography>
                        </Box>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}
